import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { locale } from "@util/locale/locale";
import { Router, NavigationEnd } from "@angular/router";
import { AccountsService, Login } from "@services/accounts.service";
import { Account } from "@interfaces/account";
import { combineLatest, Observable, of } from "rxjs";
import { NotificationService } from "@services/notification.service";
import { NotificationMessageLogService } from "@services/notification-message-log.service";
import { Notification } from "@interfaces/notifications";
import { MatDialog } from "@angular/material/dialog";
import { ReservationComponent } from "@web/app/components/dialogs/reservation/reservation.component";
import { LoadingComponent } from "@web/app/components/loading/loading.component";
import { DownloadItem, WsDownloadsService } from "@web/app/services/ws-downloads.service";
import { environment } from "@env/environment";
import { map, switchMap, filter } from "rxjs/operators";
import { GoogleAuthService } from "@services/dws/google-auth.service";
import { HostCompanyService } from "@services/host-company.service";
import { observe } from "@web/util/loading/loading";
import { feature } from "projects/web/src/app/modules/feature-toggle/feature-toggle.util";
import { AuthenticateAdminService, Authorization, CloudinaryUploadService, CrmOrdersService, DWSLanguagesService, ReceiptsService } from "@services/dws";
import {
  navigateToOrderInfoBrowse,
  ORDER_INFO_V2_SEGMENT,
} from "@web/app/pages/orders/order-info-navigation.util";
import { marker } from "@biesbjerg/ngx-translate-extract-marker";
import { SidenavContentService, SidenavContent } from "./services/sidenav-content.service";
import { toast } from "@web/util/toast";
import { tr } from "@util/tr";
import { OutlookAuthService } from "@services/dws/outlook-auth.service";
import { PreordersService } from "@services/dws/preorders.service";
import { GiftsService } from "@services/dws/gifts.service";
import { formatUtcTimestampLocal, parseUtcTimestamp } from "@util/utc-timestamp";
import { SelfAccount } from "@interfaces/dws/self-account";
import { WineSuiteParametersService } from "@services/dws/wine-suite-parameters.service";
import { EventBusEvent, EventBusService } from "./services/event-bus.service";
import { MatButtonToggleChange } from "@angular/material/button-toggle";
import { datadogRum } from "@datadog/browser-rum";
import { WsPageHeaderComponent } from "@web/app/components/ws-page-header/ws-page-header.component";
import { WsNavigatorService } from "./services/ws-navigator.service";
import { ThemeService } from "./services/theme.service";
import { ViewportScroller } from "@angular/common";
import { WineryInfoService } from "@services/dws/winery-info-service";
import {
  WSToastComponent,
  WSNoticeData,
} from "@web/app/components/wstoast/wstoast.component";
import { MatSnackBar } from "@angular/material/snack-bar";
import { WsBannerPreviewService } from "./services/ws-banner-preview.service";
import { GiftDetailComponent } from "./components/dialogs/gift-detail/gift-detail.component";
import {
  TrackedWineSuiteJob,
  WineSuiteJobsAsyncTrackerService,
} from "@services/winesuite-jobs-async-tracker.service";
import { getNotificationDisplay } from "@util/notifications-display.util";
import {
  getNextNotificationExpireAt,
  isNotificationWithinSchedule,
} from "@web/app/pages/admin/notifications-manager-standalone/notifications-banner-schedule.util";
import { isSystemStatusMessage } from "@services/system-status-messages.util";
import { resolveBannerCtaLabel } from "@web/app/pages/admin/notifications-manager-standalone/notifications-banner-target.util";

declare let versionApp: any;

marker("Cellar settings");
marker("Transactional emails");
marker(
  "Your Google Calendar account has been successfully associated to the winery!"
);
marker(
  "There was an error associating your Google Calendar account to the winery. Try again later."
);
marker(
  "Your Outlook Calendar account has been successfully associated to the winery!"
);
marker(
  "There was an error associating your Outlook Calendar account to the winery. Try again later."
);
marker("Link Outlook Calendar");
marker("Gifts");

function launchHelp() {
  window.document.getElementById("help-button")?.click();
}

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements AfterContentInit, OnDestroy {
  title = "winery-saas";
  account$: Observable<Account | null>;
  selfAccount$: Observable<SelfAccount | null>;
  /** WS-4276: name of the open winery, set only for accounts linked to 2+ wineries. */
  currentWineryName: string | null = null;
  adminAuth$: Observable<Authorization | null>;
  /** Account with SUPERADMIN system permission (winery session). */
  isAccountSuperAdmin$: Observable<boolean>;
  notifications$: Observable<Notification[] | null> = of(null);
  messages$: Observable<Notification[] | null> = of(null);
  asyncJobsRunningCount$: Observable<number> = of(0);
  asyncJobs$: Observable<TrackedWineSuiteJob[]> = of([]);
  newNotification = 0;
  newMessages = 0;
  openCrm: boolean = false;
  openMarketing: boolean = false;
  openSales: boolean = false;
  openManagement: boolean = false;
  openExperiencesAndProducts: boolean = false;
  version = versionApp;
  versionPostfix: string = environment.values["version_postfix"];
  versionRibbon: string = environment.values["version_ribbon"];
  dwsPro: boolean = false;
  adminModule = false;
  wineryId!: string;
  adminAsWinery: boolean = false;
  haveBanner: boolean = false;
  bannerText: string = "";
  showBanner: boolean = false;
  showBannerAdmin: boolean = false;
  /** Banner da messaggi attivi (tutti quelli non letti/chiusi, impilati in header). */
  messageBanners: WSNoticeData[] = [];
  /** Anteprima admin (sostituisce temporaneamente i banner messaggi in shell). */
  previewBanner: WSNoticeData | null = null;
  /** Evita aperture duplicate dello stesso toast mentre lo snackbar è aperto. */
  private pendingAutoToastMessageId: string | undefined;
  /** Evita POST READ duplicati nella stessa sessione (polling messages$). */
  private readonly adminMessageReadLoggedIds = new Set<string>();
  onlyUnreadedNotification: boolean = false;
  onlyUnreadedMessages: boolean = false;
  pageNotification: number = 1;
  pageMessages: number = 1;
  noMoreNotifications: boolean = false;
  notificationsLoading = false;
  noMoreMessages: boolean = false;
  messagesLoading = false;
  private messagesScrollLoadPending = false;
  private lastMessagesResponse: Notification[] = [];
  private bannerExpiryTimer: ReturnType<typeof setTimeout> | null = null;
  isSidenavOpen: boolean = localStorage.getItem("ws-menu-open") != "false";
  sidenavContent: SidenavContent | null = null;

  /**
   * Cached login from {@link AccountsService.login$}. Used so authenticated users keep header/sidenav
   * during URL transitions (e.g. Google sign-in / lazy routes) where `router.url` may still be `/login`.
   */
  private authenticatedLogin: Login | null = null;
  private sessionLocaleChecked = false;

  @ViewChild(WsPageHeaderComponent) wsPageHeader!: WsPageHeaderComponent; // Access wsPageHeader component
  logoImage: string | undefined;
  showLogo: boolean = false;
  showThemeSelector: boolean = false;

  get isLoading(): boolean {
    return LoadingComponent.loading;
  }

  get isIntegrations(): boolean {
    return this.router.url === "/integrations";
  }

  get loginPageClass(): string {
    const url = this.router.url;

    if (this.isSelectContextPending((url || "").split("?")[0].split("#")[0])) {
      return "login-page";
    }

    if (this.authenticatedLogin) {
      return "";
    }

    if (url === "/login" || url.startsWith("/login/")) {
      return "login-page";
    }

    return "";
  }

  downloadProgressPercent(item: DownloadItem): number {
    return this.downloadsService.progressPercent(item);
  }

  constructor(
    translate: TranslateService,
    public router: Router,
    private accounts: AccountsService,
    private notifications: NotificationService,
    private dialog: MatDialog,
    private googleAuthService: GoogleAuthService,
    private hostCompanyService: HostCompanyService,
    private cd: ChangeDetectorRef,
    private authenticationService: AuthenticateAdminService,
    private outlookAuthService: OutlookAuthService,
    private preorderService: PreordersService,
    private eventBusService: EventBusService,
    private wineSuiteParametersService: WineSuiteParametersService,
    private wsNavigator: WsNavigatorService,
    private themeService: ThemeService,
    private viewportScroller: ViewportScroller,
    private sidenavContentService: SidenavContentService,
    private wineryInfoService: WineryInfoService,
    private cloudinaryUploadService: CloudinaryUploadService,
    private wsBannerPreviewService: WsBannerPreviewService,
    private snackBar: MatSnackBar,
    private notificationMessageLogService: NotificationMessageLogService,
    private orderService: CrmOrdersService,
    private giftsService: GiftsService,
    private receiptsService: ReceiptsService,
    public downloadsService: WsDownloadsService,
    private wineSuiteJobsAsyncTracker: WineSuiteJobsAsyncTrackerService,
    private dwsLanguagesService: DWSLanguagesService
  ) {
    translate.setDefaultLang("en");

    const l = locale();
    if (l.stored != l.locale) location.reload();
    const bootLocale = l.locale;

    // Ensure English translations are loaded first, then switch to the target language
    translate.getTranslation("en").subscribe(() => {
      translate.use(locale().locale);
    });
    this.account$ = this.accounts.account$.asObservable();
    this.selfAccount$ = this.accounts.selfAccount$.asObservable();
    this.accounts.login$.subscribe((login) => {
      this.currentWineryName = this.accounts.multiWineryName(login);
      if (login && this.isAccountSuspended(login)) {
        this.accounts.logout();
        if (!this.router.url.startsWith("/login")) {
          void this.router.navigate(["/login"], {
            replaceUrl: true,
            queryParams: { suspended: 1 },
          });
        }
        return;
      }
      const accountLang = this.dwsLanguagesService.accountLanguageIso(login?.selfAccount);
      if (!this.sessionLocaleChecked) {
        this.sessionLocaleChecked = true;
        if (
          login &&
          accountLang &&
          accountLang !== bootLocale &&
          !this.router.url.startsWith("/login")
        ) {
          location.reload();
          return;
        }
      }
      this.authenticatedLogin = login;
      this.cd.markForCheck();
    });
    this.isAccountSuperAdmin$ = this.accounts.login$.pipe(
      map((login) => this.accounts.hasSuperAdminPermission(login))
    );

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.scrollToTop();
        this.cd.markForCheck();
      }
      this.cd.markForCheck();
    });

    this.accounts.company$.subscribe((company) => {
      if (company) {
        this.dwsPro = company.dws_pro_enabled;
        this.wineryId = company.id;
      }
    });

    this.notifications$ = this.notifications.notification$.asObservable();
    this.notifications.totalUnreadedNotifications$.subscribe((response) => {
      this.newNotification = response! || 0;
    });
    
    //this.notifications$.subscribe((response) => {
    //  this.newNotification = response!.reduce((accumulator, notification) => {
    //    if (!notification.viewed_at) return accumulator + 1;
    //    return accumulator;
    //  }, 0);
    //});

    this.messages$ = this.notifications.messages$.asObservable();
    this.notifications.totalUnreadedMessages$.subscribe((response) => {
      this.newMessages = response! || 0;
    });

    this.adminAuth$ = this.authenticationService.authorization$.asObservable();
    this.authenticationService.authorization$.subscribe((auth) => {
      this.adminModule = !!auth;
    });
    this.accounts.authorization$.subscribe((auth: any) => {
      this.adminAsWinery = auth?.is_admin && auth?.is_winery;
      this.showBannerAdmin = this.adminAsWinery;
    });
    this.wsBannerPreviewService.bannerPreview$.subscribe((bannerData) => {
      this.previewBanner = bannerData;
      this.updateBannerShellVisibility();
    });
    (window as any)["__feature"] = feature;

    try {
      accounts.account$.subscribe((account) => {
        if (account) {
          if (environment.name == "production") {
            var nn = account as any;
            datadogRum.setUser({ id: nn.company?.id, email: account.email });
            datadogRum.init({
              applicationId: "ccb437e3-ba8d-4fb5-99c4-de4d9bfe9683",
              clientToken: "pub34f8092272592d199216e4f1992f742f",
              // `site` refers to the Datadog site parameter of your organization
              // see https://docs.datadoghq.com/getting_started/site/
              site: "datadoghq.eu",
              service: "dws",
              env: environment.name,
              // Specify a version number to identify the deployed version of your application in Datadog
              // version: '1.0.0',
              sessionSampleRate: 25,
              sessionReplaySampleRate: 20,
              trackUserInteractions: true,
              trackResources: true,
              trackLongTasks: true,
              defaultPrivacyLevel: "mask-user-input",
            });
            datadogRum.startSessionReplayRecording();
          }
          this.loadImages(account.company?.id);
        }
      });
    } catch (e) {
      console.error(e);
    }
  }

  ngAfterContentInit() {
    // Initialize theme service
    this.themeService.listenForSystemChanges();

    // Subscribe to sidenav content service
      this.sidenavContentService.sidenavContent$.subscribe((content: SidenavContent | null) => {
        this.sidenavContent = content;
        this.cd.detectChanges();
      });

      // Perform initial ping on page load
    this.accounts.doPing();

    setTimeout(() => {
      observe(this.hostCompanyService.dwsProEnabled()).subscribe((enabled) => {
        this.dwsPro = enabled;
        this.cd.detectChanges();
      });
      this.refreshNotifications();
      this.refreshNotificationsMex();
    }, 500);
    setTimeout(() => {
      this.wineSuiteParametersService
        .showWineSuiteParameters()
        .subscribe((x) => {
          if (!!!!x.bannerContent) {
            this.bannerText = x.bannerContent;
            this.haveBanner = true;
            this.showBanner = true;
          }
        });
    }, 2500);

    this.notifications$ = this.notifications.notification$.asObservable().pipe(
      map((notifications) => {
        return [...notifications].sort(
          (a, b) =>
            (parseUtcTimestamp(b.send_at)?.getTime() ?? 0) -
            (parseUtcTimestamp(a.send_at)?.getTime() ?? 0)
        );
      })
    );
    this.notifications.totalUnreadedNotifications$.subscribe((response) => {
      this.newNotification = response! || 0;
    });

    this.messages$ = this.notifications.messages$.asObservable();
    this.notifications.totalUnreadedMessages$.subscribe((response) => {
      this.newMessages = response! || 0;
    });
    this.messages$.subscribe((response) => {
      let autoToastShownThisTick = false;
      if (response) {
        this.syncMessageBannersFromResponse(response);
      }
      for (const message of response ?? []) {
        if (isSystemStatusMessage(message)) {
          continue;
        }
        if (
          !!!!message.payload &&
          Object.keys(message.payload).length > 0 &&
          isNotificationWithinSchedule(message)
        ) {
          if (message.payload.type === "banner") {
            continue;
          } else if (
            message.payload.type === "toast" &&
            message.payload.source === "admin-notifications-manager" &&
            !!!message.viewed_at &&
            message.id != null
          ) {
            const mid = String(message.id);
            if (this.pendingAutoToastMessageId === mid) {
              continue;
            }
            if (!autoToastShownThisTick) {
              this.showAdminAutoToast(message);
              autoToastShownThisTick = true;
            }
          } else if (message.payload.type === "messageBlue") {
            if (!!!message.viewed_at) {
              this.sendMessageWSBlue(message);
            }
          }
        }
      }
    });
    this.notifications.messageBannerDismissed$.subscribe((dismissedId) => {
      this.messageBanners = this.messageBanners.filter(
        (b) => b.notificationId !== dismissedId
      );
      this.updateBannerShellVisibility();
      this.cd.detectChanges();
    });
    if (environment.name !== 'production' && environment.name !== 'staging') {
      this.showThemeSelector = true;
    }
  }

  // Load winery images
  loadImages(wineryId: string | undefined) {
    if (wineryId) {
      this.wineryInfoService.getWineryPhotos(wineryId).subscribe({
        next: (imageData) => {
          if (imageData.logo) {
            this.logoImage = this.cloudinaryUploadService.getImageUrlWithTransformation(
              imageData.logo,
              "w_40,h_40,c_fill,r_max,q_auto,f_auto"
            );
          }
        },
        error: (error) => {
          console.error("Error loading winery images:", error);
        },
      });
    }
  }

  // Load logo
  onLogoLoad() {
    this.showLogo = true;
  }

  /**
   * WS-4276 "Cambia cantina": like a logout followed by a login without password (the session token is
   * reused), then "Scegli la cantina". No confirmation: unsaved page data is lost as with Logout.
   */
  async switchWineryClicked() {
    this.adminMessageReadLoggedIds.clear();
    this.sidenavContentService.closeContent();
    await this.router.navigate(["/login/select-context"], {
      replaceUrl: true,
      queryParams: { switch: 1 },
    });
  }

  async logoutClicked() {
    this.adminMessageReadLoggedIds.clear();
    this.accounts.logout();
    // Clear any admin authentication as well
    this.authenticationService.logout();
    // Clear any sidenav content
    this.sidenavContentService.closeContent();
    // Navigate with replaceUrl to prevent back navigation
    await this.router.navigate(["/login"], { replaceUrl: true });
    // Force page reload to ensure clean state
   // window.location.reload();
  }

  async logoutAdminClicked() {
    this.authenticationService.logout();
    // Clear any sidenav content
    this.sidenavContentService.closeContent();
    // Navigate with replaceUrl to prevent back navigation
    await this.router.navigate(["/login"], { replaceUrl: true });
    // Force page reload to ensure clean state
    window.location.reload();
  }

  openNotification(notification: Notification) {
    this.notificationDismiss(notification.id!);
    const display = getNotificationDisplay(notification);

    if (display?.entityType === "reservation" && display.entityId) {
      this.openReservationNotification(notification, display.entityId);
      return;
    }
    if (display?.entityType === "self_order" && display.entityId) {
      this.openSelfOrderNotification(display.entityId, notification);
      return;
    }
    if (display?.entityType === "gift" && display.entityId) {
      this.openGiftNotification(display.entityId);
      return;
    }
    if (display?.entityType === "order" && display.entityId) {
      this.openOrderNotification(display.entityId);
      return;
    }

    if (this.notificationIsReservation(notification)) {
      const reservationId =
        notification.payload.tokens?.reservation?.id ||
        notification.payload.personalizations?.[0]?.dynamic_template_data?.reservation?.id;
      if (reservationId) {
        this.openReservationNotification(notification, reservationId);
      } else {
        console.warn("No reservation ID available from tokens or fallback");
      }
    } else if (this.notificationIsSelfOrder(notification)) {
      const fallback =
        notification?.payload?.personalizations?.[0]?.dynamic_template_data?.self_order;
      const preorder =
        notification?.payload?.tokens?.preorder || fallback || notification?.payload?.event;
      if (preorder?.id) {
        this.openSelfOrderNotification(preorder.id, notification);
      } else {
        console.error("No self-order ID found in tokens or fallback");
      }
    } else if (this.notificationIsGift(notification)) {
      const giftId =
        notification.payload?.tokens?.gift?.id ??
        notification.payload?.event?.gift_id;
      if (giftId) {
        this.openGiftNotification(giftId);
      } else {
        console.warn("No gift ID in notification payload");
      }
    }
  }

  private openReservationNotification(notification: Notification, reservationId: string) {
    LoadingComponent.loading = true;
    combineLatest([this.notifications.get(notification.id!)])
      .subscribe({
        next: ([_]) => {
          this.dialog.open(ReservationComponent, {
            width: "98vw",
            maxWidth: "1150px",
            data: { reservationId },
          });
        },
        error: (err) => {
          console.error("Error fetching reservation notification:", err);
        },
      })
      .add(() => (LoadingComponent.loading = false));
  }

  private openSelfOrderNotification(preorderId: string, notification: Notification) {
    this.orderService.getByCode(preorderId).subscribe({
      next: async (order) => {
        await navigateToOrderInfoBrowse(this.router, this.receiptsService, order);
      },
      error: (_) => {
        const preorder =
          notification?.payload?.tokens?.preorder ||
          notification?.payload?.personalizations?.[0]?.dynamic_template_data?.self_order ||
          notification?.payload?.event;
        this.preorderService.get(preorderId).subscribe({
          next: async (_) => {
            try {
              const tree = this.router.createUrlTree(["/pos/self-order/", preorderId], {
                queryParams: { channelId: preorder?.channelId || preorder?.channel_id },
              });
              await this.router.navigateByUrl(tree);
            } catch (e) {
              console.error("Error opening self order URL:", e);
            }
          },
          error: (err) => {
            console.error("Error fetching self order:", err);
            toast(tr("This preorder has been promoted or does not exist"));
          },
        });
      },
    });
  }

  private openOrderNotification(orderId: string) {
    void this.router.navigate(["/orders", orderId, ORDER_INFO_V2_SEGMENT]);
  }

  private openGiftNotification(giftId: string) {
    observe(this.giftsService.getGiftById(giftId)).subscribe(
      (gift) => {
        this.dialog.open(GiftDetailComponent, {
          width: "90%",
          minWidth: "320px",
          maxWidth: "1000px",
          maxHeight: "96vh",
          panelClass: "gift-detail-dialog-responsive",
          data: { gift },
        });
      },
      (error) => {
        if (error?.status === 404) {
          toast(tr("Cannot find this gift."));
        } else {
          toast(tr("Something went wrong"));
        }
        console.error(error);
      }
    );
  }

  openMessage(notification: Notification) {
    if (isSystemStatusMessage(notification)) {
      this.notificationDismiss(notification.id!);
      return;
    }
    var payload = notification.payload;
    var type = payload.type;
    this.notificationDismiss(notification.id!);
    var show_notification = payload.show_notification;
    if (type === "toast") {
      const data = this.mapNotificationToNoticeData(notification);
      data.displayType = "toast";
      if (notification.id) {
        data.notificationId = notification.id;
      }
      this.snackBar.openFromComponent(WSToastComponent, {
        duration: data.dismissible === false ? undefined : 6000,
        verticalPosition: "bottom",
        horizontalPosition: "right",
        panelClass: ["ws-custom-toast-panel", "ws-toast-pos-bottom-right"],
        data,
      });
    } else if (type === "banner") {
      const data = this.mapNotificationToBannerData(notification);
      const nid = data.notificationId;
      if (
        !nid ||
        !this.messageBanners.some((b) => b.notificationId === nid)
      ) {
        this.messageBanners = [...this.messageBanners, data];
      }
      this.updateBannerShellVisibility();
      if (
        payload?.source === "admin-notifications-manager" &&
        !this.isPermanentBanner(notification)
      ) {
        this.logAdminMessageReadOnce(notification.id);
      }
    } else if (type === "messageBlue") {
      this.sendMessageWSBlue(notification);
    }
  }

  closeTopBanner() {
    this.previewBanner = null;
    this.wsBannerPreviewService.clear();
    this.updateBannerShellVisibility();
  }

  /** Banner visibili in header: anteprima admin oppure tutti i banner messaggi attivi. */
  get displayBanners(): WSNoticeData[] {
    if (this.previewBanner) {
      return [this.previewBanner];
    }
    return this.messageBanners;
  }

  trackBannerById(_index: number, banner: WSNoticeData): string {
    return banner.notificationId || `banner-${_index}`;
  }

  private updateBannerShellVisibility(): void {
    this.showBanner = this.displayBanners.length > 0;
    this.haveBanner = this.showBanner || !!this.bannerText;
  }

  private syncMessageBannersFromResponse(response: Notification[]): void {
    this.lastMessagesResponse = response;
    const candidates = this.collectActiveBannerNotifications(response);
    this.messageBanners = candidates.map((message) => {
      if (
        message.payload?.source === "admin-notifications-manager" &&
        !this.isPermanentBanner(message)
      ) {
        this.logAdminMessageReadOnce(message.id);
      }
      return this.mapNotificationToBannerData(message);
    });
    if (candidates[0]) {
      this.bannerText =
        candidates[0].title + " - " + candidates[0].message;
    } else if (!this.previewBanner) {
      this.bannerText = "";
    }
    this.updateBannerShellVisibility();
    this.scheduleBannerExpiryRefresh(response);
    this.cd.markForCheck();
  }

  private collectActiveBannerNotifications(
    response: Notification[]
  ): Notification[] {
    const bannerCandidates: Notification[] = [];
    for (const message of response) {
      if (isSystemStatusMessage(message)) {
        continue;
      }
      if (
        !!!!message.payload &&
        Object.keys(message.payload).length > 0 &&
        isNotificationWithinSchedule(message) &&
        message.payload.type === "banner" &&
        !!!message.viewed_at
      ) {
        bannerCandidates.push(message);
      }
    }
    bannerCandidates.sort((a, b) => this.compareBannerTargetPriority(a, b));
    return bannerCandidates;
  }

  private clearBannerExpiryTimer(): void {
    if (this.bannerExpiryTimer != null) {
      clearTimeout(this.bannerExpiryTimer);
      this.bannerExpiryTimer = null;
    }
  }

  private scheduleBannerExpiryRefresh(response: Notification[]): void {
    this.clearBannerExpiryTimer();

    const nextExpireAt = getNextNotificationExpireAt(response);
    if (!nextExpireAt) {
      return;
    }

    const delay = Math.max(0, nextExpireAt.getTime() - Date.now()) + 100;
    this.bannerExpiryTimer = setTimeout(() => {
      this.bannerExpiryTimer = null;
      this.syncMessageBannersFromResponse(this.lastMessagesResponse);
    }, delay);
  }

  ngOnDestroy(): void {
    this.clearBannerExpiryTimer();
  }

  private mapNotificationToNoticeData(notification: Notification): WSNoticeData {
    const language = locale().locale || "en";
    const multilingualContent = notification?.payload?.multilingualContent || {};
    const localizedContent = multilingualContent[language] || multilingualContent["en"] || {};
    const tipologia = notification?.payload?.tipologia;
    const displayType =
      notification?.payload?.type === "toast" ? "toast" : "banner";

    return {
      title: localizedContent.title || notification.title || "",
      message: localizedContent.description || notification.message || "",
      closeLabel: resolveBannerCtaLabel(multilingualContent, language),
      ctaUrl: notification?.payload?.ctaUrl,
      icon: notification?.payload?.icona || 'info',
      dismissible: notification?.payload?.canClose !== false,
      displayType,
      variant: this.mapPayloadTipologiaToVariant(tipologia),
      notificationId: notification.id || undefined,
    };
  }

  private mapNotificationToBannerData(notification: Notification): WSNoticeData {
    return {
      ...this.mapNotificationToNoticeData(notification),
      displayType: "banner",
    };
  }

  /**
   * True se il payload indica target globale (tutte le cantine); false se specifico su una o più cantine.
   * Allinea a payload admin: toAll / target.type.
   */
  private isGlobalBannerPayload(payload: Notification["payload"]): boolean {
    if (!payload || typeof payload !== "object") {
      return true;
    }
    const p = payload as Record<string, unknown>;
    if (p["target"] && typeof p["target"] === "object") {
      const t = (p["target"] as Record<string, unknown>)["type"];
      if (t === "specific") {
        return false;
      }
      if (t === "all") {
        return true;
      }
    }
    if (p["toAll"] === false) {
      return false;
    }
    if (p["toAll"] === true) {
      return true;
    }
    return true;
  }

  /** Priorità: cantina specifica prima del globale; a parità, più recente (send_at). */
  private compareBannerTargetPriority(a: Notification, b: Notification): number {
    const aGlobal = this.isGlobalBannerPayload(a.payload);
    const bGlobal = this.isGlobalBannerPayload(b.payload);
    if (aGlobal !== bGlobal) {
      return aGlobal ? 1 : -1;
    }
    const aT = parseUtcTimestamp(a.send_at)?.getTime() ?? 0;
    const bT = parseUtcTimestamp(b.send_at)?.getTime() ?? 0;
    return bT - aT;
  }

  /** Banner non chiudibile: nessuna visualizzazione READ nelle statistiche admin. */
  private isPermanentBanner(notification: Notification): boolean {
    const payload = notification?.payload;
    return payload?.type === "banner" && payload?.canClose === false;
  }

  /** Registra una visualizzazione (READ) per statistiche admin; idempotente lato server per account+notifica. */
  private logAdminMessageReadOnce(notificationId: string | number | undefined | null): void {
    if (notificationId === undefined || notificationId === null) {
      return;
    }
    const id = String(notificationId);
    if (id === "" || this.adminMessageReadLoggedIds.has(id)) {
      return;
    }
    this.adminMessageReadLoggedIds.add(id);
    this.notificationMessageLogService.log(id, "READ").subscribe({
      error: () => this.adminMessageReadLoggedIds.delete(id),
    });
  }

  private showAdminAutoToast(message: Notification): void {
    const id = message.id != null ? String(message.id) : undefined;
    if (!id) {
      return;
    }
    this.pendingAutoToastMessageId = id;
    this.wsBannerPreviewService.clear();
    const data = this.mapNotificationToNoticeData(message);
    data.displayType = "toast";
    data.notificationId = id;

    const ref = this.snackBar.openFromComponent(WSToastComponent, {
      duration: data.dismissible === false ? undefined : 6000,
      verticalPosition: "bottom",
      horizontalPosition: "right",
      panelClass: ["ws-custom-toast-panel", "ws-toast-pos-bottom-right"],
      data,
    });
    this.logAdminMessageReadOnce(id);
    ref.afterDismissed().subscribe(() => {
      this.pendingAutoToastMessageId = undefined;
      observe(this.notifications.markAsViewed(id)).subscribe({
        next: () => this.notifications.markMessageBannerDismissedLocally(id),
        error: () => this.notifications.markMessageBannerDismissedLocally(id),
      });
    });
  }

  private mapPayloadTipologiaToVariant(tipologia: string): WSNoticeData["variant"] {
    switch (tipologia) {
      case "warning":
        return "warning";
      case "evento":
        return "evento";
      case "error":
        return "error";
      case "success":
        return "success";
      case "info":
      default:
        return "info";
    }
  }

  hideBanner(event: MouseEvent) {
    var type = event.type;
    if (type == "mouseover") {
      this.showBanner = false;
    } else if (type == "mouseleave") {
      setTimeout(() => {
        this.showBanner = true;
      }, 2000);
    }
  }

  hideBannerAdmin(event: MouseEvent) {
    var type = event.type;
    if (type == "mouseover") {
      this.showBannerAdmin = false;
    } else if (type == "mouseleave") {
      setTimeout(() => {
        this.showBannerAdmin = true;
      }, 2000);
    }
  }

  openManual() {
    window.open(environment.values["manual-url"], "_blank");
  }

  async openContacts() {
    await this.router.navigate(["/contacts"]);
  }

  async openSegments() {
    await this.router.navigate(["/segments"]);
  }

  async openImportContact() {
    await this.router.navigate(["/winery-contact-import"]);
  }

  async openTags() {
    await this.router.navigate(["/tags"]);
  }

  async openProducts() {
    await this.router.navigate(["/product-catalog/products"]);
  }

  async openProductCategory() {
    await this.router.navigate(["/product-categories"]);
  }

  async openMarketingTab(tab: string) {
    await this.router.navigate(["/marketing", tab]);
    this.openMarketing = !this.openMarketing;
  }

  restartState() {
    this.openCrm = false;
    this.openSales = false;
    this.openMarketing = false;
    this.openManagement = false;
    this.openExperiencesAndProducts = false;
  }

  restartStateCrm() {
    this.openSales = false;
    this.openMarketing = false;
    this.openManagement = false;
    this.openExperiencesAndProducts = false;
    if (this.openCrm) {
      this.openCrm = true;
    }
  }

  restartStateSales() {
    this.openCrm = false;
    this.openMarketing = false;
    this.openManagement = false;
    this.openExperiencesAndProducts = false;
    if (this.openSales) {
      this.openSales = true;
    }
  }

  restartStateMarketing() {
    this.openCrm = false;
    this.openSales = false;
    this.openManagement = false;
    this.openExperiencesAndProducts = false;
    if (this.openMarketing) {
      this.openMarketing = true;
    }
  }

  restartStateManagement() {
    this.openCrm = false;
    this.openSales = false;
    this.openMarketing = false;
    this.openExperiencesAndProducts = false;
    if (this.openManagement) {
      this.openManagement = true;
    }
  }

  restartStateExperiencesAndProducts() {
    this.openCrm = false;
    this.openSales = false;
    this.openMarketing = false;
    this.openManagement = false;
    if (this.openExperiencesAndProducts) {
      this.openExperiencesAndProducts = true;
    }
  }

  refreshNotifications() {
    if (this.notificationsLoading) {
      return;
    }
    this.notificationsLoading = true;
    this.notifications
      .list(
        this.pageNotification,
        10,
        true,
        this.onlyUnreadedNotification == false
      )
      .subscribe({
        next: (result) => {
          this.noMoreNotifications = !(result && result.length === 10);
          this.notificationsLoading = false;
        },
        error: (error) => {
          console.error("[refreshNotifications] Error:", error);
          this.notificationsLoading = false;
        },
      });
  }

  refreshNotificationsMex() {
    if (this.messagesLoading) {
      return;
    }
    this.messagesLoading = true;
    this.notifications
      .listMexs(this.pageMessages, 10, true, this.onlyUnreadedMessages == false)
      .subscribe({
        next: (result) => {
          this.noMoreMessages = !(result && result.length === 10);
          this.messagesLoading = false;
          this.messagesScrollLoadPending = false;
        },
        error: () => {
          this.messagesLoading = false;
          this.messagesScrollLoadPending = false;
        },
      });
  }

  async notificationsAllDismiss() {
    this.notifications.markAllAsViewed().subscribe();
  }

  async messagesAllDismiss() {
    this.notifications.markAllAsViewedMex().subscribe();
  }

  notificationsClosed() {
    // DO NOTHING
  }

  messaggesClosed() {
    // DO NOTHING
  }

  async notificationDismiss(notificationId: string) {
    this.notifications.markAsViewed(notificationId).subscribe();
  }

  async discontCodeClicked() {
    await this.router.navigate(["/discount"]);
  }

  async giftClicked() {
    await this.router.navigate(["/gifts"]);
  }

  async reservationsClicked() {
    await this.router.navigate(["/reservations"]);
  }

  async openAiAgentFullPage() {
    try {
      await this.router.navigateByUrl("/settings/ai-agent");
    } catch {
      await this.router.navigate(["/settings", "ai-agent"]);
    }
  }

  /**
   * @deprecated New method is in the Integrations page
   */
  async googleCalendarLinkClicked() {
    const credentials = await this.googleAuthService.authorize().toPromise();
    if (credentials.error == "popup_closed_by_user") return;
    LoadingComponent.loading = true;
    this.googleAuthService
      .link(credentials, this.wineryId)
      .subscribe(
        (result) => {
          toast(
            tr(
              "Your Google Calendar account has been successfully associated to the winery!"
            )
          );
          console.log("GoogleCalendar: ", result);
        },
        (err) => {
          toast(
            tr(
              "There was an error associating your Google Calendar account to the winery. Try again later."
            )
          );
          console.log("error", err);
        }
      )
      .add(() => (LoadingComponent.loading = false));
  }

  /** True on the winery picker, or while the logged-in account has not picked a winery yet. */
  private isSelectContextPending(pathOnly: string): boolean {
    if (pathOnly === "/login/select-context" || pathOnly.startsWith("/login/select-context/")) {
      return true;
    }
    return this.accounts.needsLoginContextSelection(this.authenticatedLogin);
  }

  get isPublicRoute() {
    const raw = this.router.url || "";
    const currentUrl = raw.split("?")[0].split("#")[0];

    const explicitPublicPaths =
      currentUrl === "/login" ||
      currentUrl.startsWith("/login/") ||
      currentUrl.startsWith("/registration") ||
      currentUrl.startsWith("/unsubscribe") ||
      currentUrl.startsWith("/reset-password") ||
      currentUrl.startsWith("/set-password") ||
      currentUrl.startsWith("/mfa-reenroll") ||
      currentUrl.startsWith("/privacy");

    // Multi-winery account still has to pick a winery: no app chrome (menu links would bounce back here).
    if (this.isSelectContextPending(currentUrl)) {
      return true;
    }

    // Keep header/sidenav visible during post-login handoff (e.g. Google sign-in still on /login).
    if (this.authenticatedLogin) {
      return false;
    }

    if (currentUrl === "" || currentUrl === "/") {
      return true;
    }

    return explicitPublicPaths;
  }

  private isAccountSuspended(
    login:
      | { userDWS?: { accountExpiresAt?: string | Date | null } | null }
      | null
      | undefined
  ): boolean {
    const expiresAt = this.getAccountExpiresAt(login);
    if (!expiresAt) {
      return false;
    }
    return expiresAt.getTime() < Date.now();
  }

  private getAccountExpiresAt(
    login:
      | { userDWS?: { accountExpiresAt?: string | Date | null } | null }
      | null
      | undefined
  ): Date | null {
    const raw = login?.userDWS?.accountExpiresAt;
    if (!raw) {
      return null;
    }
    const timestamp = new Date(raw).getTime();
    if (Number.isNaN(timestamp)) {
      return null;
    }
    return new Date(timestamp);
  }

  /**
   * @deprecated New method is in the Integrations page
   */
  async outlookCalendarLinkClicked() {
    let closedWindow = null;
    LoadingComponent.loading = true;
    const credentials = await this.outlookAuthService
      .authorize()
      .catch((error) => {
        closedWindow = error;
        LoadingComponent.loading = false;
      });
    LoadingComponent.loading = false;
    if (closedWindow) return;

    observe(this.outlookAuthService.link(credentials, this.wineryId)).subscribe(
      (result) => {
        toast(
          tr(
            "Your Outlook Calendar account has been successfully associated to the winery!"
          )
        );
        console.log("OutlookCalendar: ", result);
      },
      (error) => {
        toast(
          tr(
            "There was an error associating your Outlook Calendar account to the winery. Try again later."
          )
        );
        console.log("error", error);
      }
    );
  }

  notificationIsReservation(notification: Notification): boolean {
    const nameMatch =
      notification?.event_name?.toLowerCase().includes("reservation") ||
      notification?.payload?.event?.event_name
        ?.toLowerCase()
        .includes("reservation");
    const hasReservation =
      !!notification?.payload?.personalizations?.[0]?.dynamic_template_data
        ?.reservation;

    return nameMatch || hasReservation;
  }

  notificationIsSelfOrder(notification: Notification): boolean {
    const eventName = notification?.event_name?.toLowerCase() ?? "";
    const payloadEventName =
      notification?.payload?.event?.event_name?.toLowerCase() ?? "";
    const dynamicData =
      notification?.payload?.personalizations?.[0]?.dynamic_template_data;

    return (
      eventName.includes("self_order") ||
      eventName.includes("preorder") ||
      payloadEventName.includes("self_order") ||
      payloadEventName.includes("preorder") ||
      !!dynamicData?.self_order
    );
  }

  notificationIsGift(notification: Notification): boolean {
    const eventName = notification?.event_name;
    const payloadEventName = notification?.payload?.event?.event_name;
    const isGiftEvent =
      eventName === "GiftRegisteredEvent" ||
      payloadEventName === "GiftRegisteredEvent";
    const giftId =
      notification?.payload?.tokens?.gift?.id ??
      notification?.payload?.event?.gift_id;
    return isGiftEvent && !!giftId;
  }

  notificationIsNews(notification: Notification): boolean {
    return (
      notification?.event_name != undefined &&
      notification?.event_name == "news_channel"
    );
  }

  bannerAdminAcessText(): string {
    let companyName = "";
    this.accounts.company$.subscribe(
      (company) => (companyName = company?.name || "")
    );
    return `${tr("You are accessing as")} ${companyName}`;
  }

  showBannerText(): string {
    return this.bannerText;
  }

  formatDate(date: string | Date | undefined): string {
    return formatUtcTimestampLocal(date, "dd/MM HH:mm");
  }

  openHelp() {
    // OPEN THE WIDGET FORM
    eval(
      "document.querySelector(\"iframe[name='JSD widget']\").contentDocument.getElementById('help-button').click()"
    );
  }

  downloadAsyncJobReport(jobId: string): void {
    this.wineSuiteJobsAsyncTracker.downloadJobReport(jobId);
  }

  downloadAsyncJobReportCsv(event: MouseEvent, jobId: string): void {
    event.stopPropagation();
    event.preventDefault();
    this.wineSuiteJobsAsyncTracker.downloadJobReport(jobId);
  }

  downloadAsyncJobReportExcel(event: MouseEvent, jobId: string): void {
    event.stopPropagation();
    event.preventDefault();
    this.wineSuiteJobsAsyncTracker.downloadJobReportExcel(jobId);
  }

  isAsyncJobRunning(job: TrackedWineSuiteJob): boolean {
    return job.status !== "COMPLETED" && job.status !== "FAILED";
  }

  isAsyncJobReportDownloadable(job: TrackedWineSuiteJob): boolean {
    return this.wineSuiteJobsAsyncTracker.canDownloadJobReport(job);
  }

  onNotificationVisualizationClick(event: MouseEvent) {
    event.stopPropagation();
  }

  onNotificationPanelFilter(filter: "all" | "unreaded"): void {
    this.pageNotification = 1;
    this.onlyUnreadedNotification = filter === "unreaded";
    this.refreshNotifications();
  }

  onChangeVisualization(event: MatButtonToggleChange, type: string): void {
    var value = event.source.value;
    var isAll = value === "all";
    this.pageNotification = 1; //reset
    if (type === "notification") {
      this.onlyUnreadedNotification = !isAll;
      this.refreshNotifications();
    } else {
      this.onlyUnreadedMessages = !isAll;
      this.refreshNotificationsMex();
    }
  }

  onScrollDownNotification() {
    if (this.noMoreNotifications || this.notificationsLoading) {
      return;
    }
    this.pageNotification++;
    this.refreshNotifications();
  }

  onScrollDownMessages() {
    if (this.noMoreMessages || this.messagesLoading) {
      return;
    }
    this.pageMessages++;
    this.refreshNotificationsMex();
  }

  onMessagesListScroll(event: Event): void {
    if (this.noMoreMessages || this.messagesLoading || this.messagesScrollLoadPending) {
      return;
    }
    const element = event.target as HTMLElement;
    const remaining = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (remaining > 80) {
      return;
    }
    this.messagesScrollLoadPending = true;
    this.onScrollDownMessages();
  }

  sendMessageWSBlue(message: Notification) {
    if (!message) return;
    var event: EventBusEvent = {
      id: message.id!,
      name: message.event_name!,
      data: message,
    };
    this.eventBusService.publish(event, message.event_name!);
  }

  closeSidenav(force: boolean = false) {
    //sidenav.close()
    this.isSidenavOpen = force ? false : !this.isSidenavOpen;
  }

  isNewGraphic(): boolean {
    return (
      this.router.url.includes("availability") ||
      this.router.url.includes("company-management") ||
      (this.router.url.includes("calendar") &&
        !this.router.url.includes("reports-dashboard")) ||
      this.router.url.includes("integrations") ||
      this.router.url.includes("homepage") ||
      this.router.url.includes("account-detail") ||
      this.router.url.includes("marketing") ||
      this.router.url.includes("form") ||
      this.router.url.includes("experiences") ||
      this.router.url.includes("payments") ||
      this.router.url.includes("attributes") ||
      this.router.url.includes("product-catalog") ||
      this.router.url.includes("order-info-v2") ||
      this.router.url.includes("users-and-roles") ||
      this.router.url.includes("ecommerce") ||
      this.router.url.includes("shipping") ||
      this.router.url.includes("ai-agent") ||
      false
    );
  }

  hasHeader(): boolean {
    return this.wsNavigator.haveToShowTitle();
  }

  hasSubHeader(): boolean {
    return (
      this.wsNavigator.haveToShowSubtitle() ||
      this.wsNavigator.haveToShowBreadcrumb()
    );
  }

  isAdminSection(): boolean {
    return false;
  }

  /**
   * Scrolls to top of page, handling multiple scroll containers
   */
  private scrollToTop(): void {
    this.viewportScroller.scrollToPosition([0, 0]);
    setTimeout(() => {
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      }

      const contentWrapper = document.querySelector(
        ".content-wrapper-ws-internal"
      );
      if (contentWrapper) {
        contentWrapper.scrollTop = 0;
      }

      const sidenavContent = document.querySelector("mat-sidenav-content");
      if (sidenavContent) {
        sidenavContent.scrollTop = 0;
      }

      const drawerContent = document.querySelector("mat-drawer-content");
      if (drawerContent) {
        drawerContent.scrollTop = 0;
      }

      const potentialScrollers = [
        ".content-wrapper-ws-internal",
        ".content-wrapper-ws",
        "mat-sidenav-content",
        "mat-drawer-content",
        ".sidenav-container",
        '[class*="scroll"]',
      ];

      potentialScrollers.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element) => {
          if (element instanceof HTMLElement && element.scrollTop > 0) {
            console.log(`Resetting scroll for: ${selector}`, element.scrollTop);
            element.scrollTop = 0;
          }
        });
      });
    }, 10);
  }

  translateNotificationTitle(title: string): string {
    if (!title) return "";

    const titleMappings = {
      "Nuovo Gift per": "New Gift for",
      "New Gift for": "New Gift for",
      "Nuova Prenotazione": "New Reservation",
      "Nuovo SELF ORDER": "New SELF ORDER",
      "Conferma Prenotazione": "Reservation confirmed",
      "Prenotazione REVOCATA": "Reservation revoked",
      "Prenotazione RIFIUTATA": "Reservation refused",
    };

    let translatedTitle = title;

    Object.entries(titleMappings).forEach(([ogText, eng]) => {
      if (translatedTitle.includes(ogText)) {
        translatedTitle = translatedTitle.replace(ogText, tr(eng));
      }
    });

    return translatedTitle;
  }

  translateNotificationMessage(message: string): string {
    if (!message) return "";

    const messageMappings = {
      "Un nuovo self order è stato registrato":
        "A new self order has been registered",
      "La prenotazione è stata cancellata":
        "The reservation has been cancelled",
      "La prenotazione è stata modificata": "The reservation has been modified",
      "Un nuovo contatto è stato aggiunto": "A new contact has been added",
      "Regalo acquistato": "Gift purchased",
      "Nuovo ordine ricevuto": "New order received",
    };

    let translatedMessage = message;

    Object.entries(messageMappings).forEach(([ogText, eng]) => {
      if (translatedMessage.includes(ogText)) {
        translatedMessage = translatedMessage.replace(ogText, tr(eng));
      }
    });

    return translatedMessage;
  }
}